import { Link } from "react-router-dom";

export default function WidgetEmbed() {
  const configOptions = [
    {
      key: "apiUrl",
      type: "string",
      default: "''",
      description: "Your support platform URL",
    },
    {
      key: "primaryColor",
      type: "string",
      default: "'#3b82f6'",
      description: "Widget theme color (hex code)",
    },
    {
      key: "position",
      type: "string",
      default: "'bottom-right'",
      description: "'bottom-right' or 'bottom-left'",
    },
    {
      key: "welcomeMessage",
      type: "string",
      default: "'Hi! How can we help you today?'",
      description: "Initial greeting message",
    },
  ];

  const widgetFeatures = [
    "Minimizable UI",
    "AI-powered answers",
    "File attachments",
    "Custom branding",
    "Typing indicators",
    "New message notifications",
    "Conversation persistence",
    "Mobile-friendly",
  ];

  return (
    <div className="max-w-4xl space-y-6">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Widget <span className="text-gradient-ginox">Integration</span>
        </h1>
        <p className="text-xs text-muted-foreground">
          Learn how to embed the chat widget on your website
        </p>
      </div>

      {/* Quick Start Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Quick Start
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Get your chat widget up and running in minutes
          </p>
        </div>

        <div className="space-y-4">
          {/* Step 1 */}
          <div className="rounded-md border border-border/70 bg-white/[0.02] p-4">
            <h4 className="text-xs font-semibold text-foreground mb-2">
              Step 1: Add the Script
            </h4>
            <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground overflow-x-auto">
              {`<script src="https://helpapis.ginox.io/widget.js"></script>`}
            </pre>
          </div>

          {/* Step 2 */}
          <div className="rounded-md border border-border/70 bg-white/[0.02] p-4">
            <h4 className="text-xs font-semibold text-foreground mb-2">
              Step 2: Initialize the Widget
            </h4>
            <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground overflow-x-auto">
              {`<script>
  SupportWidget.init({
    apiUrl: 'https://helpapis.ginox.io',
    primaryColor: '#3b82f6',
    position: 'bottom-right',
    welcomeMessage: 'Hi! How can we help you today?'
  });
</script>`}
            </pre>
          </div>

          {/* Step 3 */}
          <div className="rounded-md border border-border/70 bg-white/[0.02] p-4">
            <h4 className="text-xs font-semibold text-foreground mb-2">
              Step 3: Test Your Widget
            </h4>
            <p className="text-xs text-muted-foreground">
              The widget will appear as a floating chat button on your website.
              Click it to start a conversation with your AI support system.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Options Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Configuration Options
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Customize the widget to match your brand and requirements
          </p>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/70">
                <th className="text-left py-2 pb-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground pr-4">
                  Option
                </th>
                <th className="text-left py-2 pb-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground pr-4">
                  Type
                </th>
                <th className="text-left py-2 pb-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground pr-4">
                  Default
                </th>
                <th className="text-left py-2 pb-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {configOptions.map((opt) => (
                <tr key={opt.key} className="border-b border-border/50">
                  <td className="py-2.5 pr-4 font-mono text-xs text-foreground">
                    {opt.key}
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-muted-foreground">
                    {opt.type}
                  </td>
                  <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">
                    {opt.default}
                  </td>
                  <td className="py-2.5 text-xs text-muted-foreground">
                    {opt.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {configOptions.map((opt) => (
            <div
              key={opt.key}
              className="rounded-md border border-border/70 bg-white/[0.02] p-3"
            >
              <div className="flex items-start justify-between mb-2">
                <span className="font-mono text-xs text-foreground">
                  {opt.key}
                </span>
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border border-border/70 bg-white/[0.03] text-muted-foreground">
                  {opt.type}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground mb-1">
                Default:{" "}
                <span className="font-mono text-foreground">{opt.default}</span>
              </p>
              <p className="text-[10px] text-muted-foreground">
                {opt.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Widget Features Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Widget Features
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {widgetFeatures.map((f, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-emerald-400 text-xs font-mono" aria-hidden="true">✓</span>
              <span className="text-xs text-muted-foreground">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Advanced Usage Card */}
      <div className="rounded-lg border border-border/70 bg-card p-5">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Advanced Usage
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Additional customization and integration options
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2">
              Custom Styling
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              You can override the default styles by adding custom CSS:
            </p>
            <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground overflow-x-auto">
              {`<style>
  .support-widget .chat-window { border-radius: 8px !important; box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important; }
  .support-widget .chat-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; color: white !important; }
</style>`}
            </pre>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2">
              Event Callbacks
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              Listen to widget events for custom integrations:
            </p>
            <pre className="font-mono text-xs bg-white/[0.03] border border-border/70 rounded-md p-3 text-foreground overflow-x-auto">
              {`SupportWidget.init({
  apiUrl: 'https://helpapis.ginox.io',
  onOpen: function() { console.log('Widget opened'); },
  onClose: function() { console.log('Widget closed'); },
  onMessage: function(message) { console.log('New message:', message); }
});`}
            </pre>
          </div>

          <div className="flex gap-3">
            <Link
              to="/widget-demo"
              className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
            >
              Try Live Demo
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
